'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "videoProps", "onPlay", "onPause", "onClose", "closed", "subtitlesUrl", "videoPlayerSize", "portrait", "aspectRatio", "onMinimize", "onExpand", "alwaysHidePlayPauseButton", "ariaLabels", "onTimeUpdate", "hasOverlay"];
import React, { useEffect } from 'react';
import { opacityBlack0 } from '@spotify-internal/encore-foundation';
import { AdsBox } from "../../AdsBox/index.mjs";
import { DEFAULT_DIMENSIONS, getVideoAspectRatio, parsePixelValue, VideoPlayerSize } from "./utils.mjs";
import { useVideo } from "../../../hooks/useVideo.mjs";
import { ASPECT_RATIOS } from "../../../utils/config.mjs";
import { ButtonPrimary } from "../../../../core/components/ButtonPrimary/index.mjs";
import { ButtonTertiary } from "../../../../core/components/ButtonTertiary/index.mjs";
import { IconFullscreen } from "../../../../core/components/Icon/icons/IconFullscreen/index.mjs";
import { IconMinimize } from "../../../../core/components/Icon/icons/IconMinimize/index.mjs";
import { IconPause } from "../../../../core/components/Icon/icons/IconPause/index.mjs";
import { IconPlay } from "../../../../core/components/Icon/icons/IconPlay/index.mjs";
import { IconSubtitles } from "../../../../core/components/Icon/icons/IconSubtitles/index.mjs";
import { IconX } from "../../../../core/components/Icon/icons/IconX/index.mjs";
import styled, { css } from "../../../../core/styled-components.mjs";
import { addColorSet, cssColorValue, cssSpacing, semanticBackgroundColors } from "../../../../core/styles/index.mjs";
var ProtectiveOverlay = styled.div.withConfig({
  displayName: "V2__ProtectiveOverlay",
  componentId: "sc-5orbei-0"
})(["position:absolute;inset:0;z-index:1;display:flex;justify-content:space-between;flex-flow:column;flex:1 1 0px;", ""], function (_ref) {
  var $hasOverlay = _ref.$hasOverlay;
  return $hasOverlay && css(["background:linear-gradient( 180deg,", ",", ",", " 45%,", " );"], cssColorValue(semanticBackgroundColors.backgroundElevatedHighlight), opacityBlack0, opacityBlack0, cssColorValue(semanticBackgroundColors.backgroundPress));
});
var ClickableOverlay = styled(ButtonTertiary).withConfig({
  displayName: "V2__ClickableOverlay",
  componentId: "sc-5orbei-1"
})(["position:absolute;inset:0;"]);
var VideoContainer = styled(AdsBox).withConfig({
  displayName: "V2__VideoContainer",
  componentId: "sc-5orbei-2"
})(["&&{justify-content:center;border:none;padding:0;overflow:hidden;", " > div > video{max-width:100%;max-height:100%;}}"], function (_ref2) {
  var width = _ref2.width,
    height = _ref2.height;
  return css(["width:", ";height:", ";"], width, height);
});
var ButtonContainer = styled.div.withConfig({
  displayName: "V2__ButtonContainer",
  componentId: "sc-5orbei-3"
})(["flex-basis:", ";display:flex;align-items:center;justify-content:center;height:1;"], function (_ref3) {
  var flexBasis = _ref3.flexBasis;
  return flexBasis ? "".concat(flexBasis, "px") : 'unset';
});
var OptionsContainer = styled.div.withConfig({
  displayName: "V2__OptionsContainer",
  componentId: "sc-5orbei-4"
})(["flex-basis:", ";display:flex;justify-content:flex-end;align-items:flex-start;padding-right:", ";padding-top:", ";> button{align-self:baseline;}"], function (_ref4) {
  var flexBasis = _ref4.flexBasis;
  return flexBasis ? "".concat(flexBasis, "px") : 'unset';
}, cssSpacing('tighter-2', '8px'), cssSpacing('tighter-2', '8px'));
var timeMouseIdleBeforeHidingPlayButton = 3000;
var defaultAriaLabels = {
  subtitlesOffLabel: 'Subtitles off',
  subtitlesOnLabel: 'Subtitles on',
  playButtonLabel: 'Play',
  pauseButtonLabel: 'Pause',
  expandVideoLabel: 'Expand video',
  minimizeVideoLabel: 'Minimize video',
  closeButtonAriaLabel: 'Close'
};
export var VideoPlayer = /*#__PURE__*/React.forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
    videoProps = _ref5.videoProps,
    onPlay = _ref5.onPlay,
    onPause = _ref5.onPause,
    onClose = _ref5.onClose,
    closed = _ref5.closed,
    subtitlesUrl = _ref5.subtitlesUrl,
    _ref5$videoPlayerSize = _ref5.videoPlayerSize,
    videoPlayerSize = _ref5$videoPlayerSize === void 0 ? VideoPlayerSize.LARGE : _ref5$videoPlayerSize,
    portrait = _ref5.portrait,
    aspectRatio = _ref5.aspectRatio,
    onMinimize = _ref5.onMinimize,
    onExpand = _ref5.onExpand,
    alwaysHidePlayPauseButton = _ref5.alwaysHidePlayPauseButton,
    _ref5$ariaLabels = _ref5.ariaLabels,
    ariaLabels = _ref5$ariaLabels === void 0 ? defaultAriaLabels : _ref5$ariaLabels,
    onTimeUpdate = _ref5.onTimeUpdate,
    hasOverlay = _ref5.hasOverlay,
    props = _objectWithoutProperties(_ref5, _excluded);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showPlayPauseButton = _React$useState2[0],
    setShowPlayPauseButton = _React$useState2[1];
  var _React$useState3 = React.useState(videoPlayerSize),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    videoSize = _React$useState4[0],
    setVideoSize = _React$useState4[1];
  var _React$useState5 = React.useState(videoSize === VideoPlayerSize.SMALL),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    videoMinimized = _React$useState6[0],
    setVideoMinimized = _React$useState6[1];
  var _React$useState7 = React.useState(false),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    show = _React$useState8[0],
    setShow = _React$useState8[1];
  var _React$useState9 = React.useState(!!subtitlesUrl),
    _React$useState10 = _slicedToArray(_React$useState9, 2),
    subtitlesOn = _React$useState10[0],
    setSubtitlesOn = _React$useState10[1];
  var _useVideo = useVideo( /*#__PURE__*/React.createElement("video", Object.assign({}, videoProps, {
      "data-testid": "video-element"
    }), /*#__PURE__*/React.createElement("track", {
      "default": true,
      src: subtitlesOn ? subtitlesUrl : '',
      kind: "captions",
      srcLang: "en"
    }))),
    _useVideo2 = _slicedToArray(_useVideo, 4),
    videoElement = _useVideo2[0],
    videoState = _useVideo2[1],
    videoControls = _useVideo2[2],
    videoRef = _useVideo2[3];
  var widthNum = parsePixelValue(props.width);
  useEffect(function () {
    if (!videoRef.current) return undefined;
    var videoElementRef = videoRef.current;
    var handleTimeUpdate = function handleTimeUpdate() {
      onTimeUpdate === null || onTimeUpdate === void 0 || onTimeUpdate(videoElementRef.currentTime);
    };
    videoElementRef.addEventListener('timeupdate', handleTimeUpdate);
    return function () {
      videoElementRef.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [onTimeUpdate, videoRef]);
  var subtitlesOffLabel = ariaLabels.subtitlesOffLabel,
    subtitlesOnLabel = ariaLabels.subtitlesOnLabel,
    playButtonLabel = ariaLabels.playButtonLabel,
    pauseButtonLabel = ariaLabels.pauseButtonLabel,
    expandVideoLabel = ariaLabels.expandVideoLabel,
    minimizeVideoLabel = ariaLabels.minimizeVideoLabel,
    closeButtonAriaLabel = ariaLabels.closeButtonAriaLabel;
  React.useImperativeHandle(ref, function () {
    return videoRef.current;
  }, [videoRef]);

  // The ProtectiveOverlay and PlayPauseButton should fade out if mouse has been idle for more than a few seconds
  // In order to do that, we need to store a timeout that restarts every time the mouse moves
  var mouseIdleTimeout = React.useRef();
  React.useEffect(function () {
    // clear timeout on component unmount
    return function () {
      if (mouseIdleTimeout.current) clearTimeout(mouseIdleTimeout.current);
    };
  }, []);
  React.useEffect(function () {
    setShow(!!closed);
  }, [closed]);
  var getVideoSize = function getVideoSize(dimension, givenWidth) {
    var videoAspectRatio = getVideoAspectRatio(aspectRatio, portrait);

    // If the width is provided, either return it, or use it to calculate height
    if (dimension === 'width' && !!givenWidth) return givenWidth;
    if (dimension === 'height' && !!givenWidth) return givenWidth / videoAspectRatio;

    // If no width is provided, check the video size variant for default values
    if (dimension === 'width') {
      // Handles deprecated portrait prop
      if (portrait) return DEFAULT_DIMENSIONS[ASPECT_RATIOS.PORTRAIT][videoSize].width;
      return DEFAULT_DIMENSIONS[videoAspectRatio][videoSize].width;
    }
    // Handles deprecated portrait prop
    if (portrait) return DEFAULT_DIMENSIONS[ASPECT_RATIOS.PORTRAIT][videoSize].height;
    return DEFAULT_DIMENSIONS[videoAspectRatio][videoSize].height;
  };
  var getFlexBasis = function getFlexBasis() {
    var height = getVideoSize('height', widthNum);
    var heightValue = height;
    return heightValue / 3;
  };
  var onClickSizeChange = function onClickSizeChange() {
    if (onExpand && videoMinimized) {
      setVideoMinimized(false);
      onExpand();
    } else if (onMinimize && !videoMinimized) {
      setVideoMinimized(true);
      onMinimize();
    } else {
      setVideoMinimized(videoSize !== VideoPlayerSize.SMALL);
      setVideoSize(videoSize === VideoPlayerSize.SMALL ? VideoPlayerSize.LARGE : VideoPlayerSize.SMALL);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, !show && /*#__PURE__*/React.createElement(VideoContainer, Object.assign({
    "data-testid": "video-container",
    className: addColorSet('invertedDark', className),
    width: "".concat(getVideoSize('width', widthNum), "px"),
    height: "".concat(getVideoSize('height', widthNum), "px")
  }, props), /*#__PURE__*/React.createElement(ProtectiveOverlay, {
    className: addColorSet('overMedia'),
    $hasOverlay: hasOverlay,
    onMouseMove: function onMouseMove(e) {
      e.preventDefault();
      setShowPlayPauseButton(true);

      // Restart the mouseIdleTimeout whenever the mouse moves.
      if (mouseIdleTimeout.current) clearTimeout(mouseIdleTimeout.current);
      mouseIdleTimeout.current = setTimeout(function () {
        return setShowPlayPauseButton(false);
      }, timeMouseIdleBeforeHidingPlayButton);
    },
    onMouseLeave: function onMouseLeave() {
      return setShowPlayPauseButton(false);
    }
  }, alwaysHidePlayPauseButton && /*#__PURE__*/React.createElement(ClickableOverlay, {
    "data-testid": "play-pause-overlay",
    "aria-label": videoState.paused ? playButtonLabel : pauseButtonLabel,
    onClick: function onClick() {
      if (videoState.paused) {
        videoControls.play();
        onPlay === null || onPlay === void 0 || onPlay();
      } else {
        videoControls.pause();
        onPause === null || onPause === void 0 || onPause();
      }
    }
  }), (videoState.paused || showPlayPauseButton) && /*#__PURE__*/React.createElement(OptionsContainer, {
    flexBasis: getFlexBasis()
  }, !!subtitlesUrl && /*#__PURE__*/React.createElement(ButtonPrimary, {
    size: "small",
    "aria-label": subtitlesOn ? subtitlesOnLabel : subtitlesOffLabel,
    iconOnly: IconSubtitles,
    colorSet: "overMedia",
    onClick: function onClick() {
      return setSubtitlesOn(!subtitlesOn);
    },
    style: {
      marginLeft: cssSpacing('tighter-2', '8px')
    }
  }), (!widthNum || onMinimize) && /*#__PURE__*/React.createElement(ButtonPrimary, {
    size: "small",
    "aria-label": videoMinimized ? expandVideoLabel : minimizeVideoLabel,
    iconOnly: videoMinimized ? IconFullscreen : IconMinimize,
    colorSet: "overMedia",
    onClick: onClickSizeChange,
    style: {
      marginLeft: cssSpacing('tighter-2', '8px')
    }
  }), !!onClose && /*#__PURE__*/React.createElement(ButtonPrimary, {
    size: "small",
    "aria-label": closeButtonAriaLabel,
    iconOnly: IconX,
    colorSet: "overMedia",
    onClick: function onClick() {
      setShow(true);
      onClose(true);
    },
    style: {
      marginLeft: cssSpacing('tighter-2', '8px')
    }
  })), /*#__PURE__*/React.createElement(ButtonContainer, {
    flexBasis: getFlexBasis()
  }, (videoState.paused || showPlayPauseButton) && !alwaysHidePlayPauseButton && /*#__PURE__*/React.createElement(ButtonPrimary, {
    "data-testid": "play-pause-button",
    "aria-label": videoState.paused ? playButtonLabel : pauseButtonLabel,
    size: "medium",
    iconOnly: videoState.paused ? IconPlay : IconPause,
    colorSet: "overMedia",
    onClick: function onClick() {
      if (videoState.paused) {
        videoControls.play();
        onPlay === null || onPlay === void 0 || onPlay();
      } else {
        videoControls.pause();
        onPause === null || onPause === void 0 || onPause();
      }
    }
  })), /*#__PURE__*/React.createElement(ButtonContainer, {
    flexBasis: getFlexBasis()
  })), videoElement));
});